import { API_BASE } from '../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Truck, 
  Building2, 
  Calculator, 
  DollarSign, 
  FileText,
  Users,
  ArrowRight,
  PlusCircle,
  TrendingUp,
  Clock,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  PieChart as PieChartIcon
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const API = API_BASE;

// Chart colors
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const ASSET_COLORS = {
  'Semi-Truck': '#3b82f6',
  'Box Truck': '#10b981',
  'Reefer Truck': '#06b6d4',
  'Pickup': '#f59e0b',
  'Trailer': '#8b5cf6',
  'Otro': '#6b7280'
};

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    try {
      const [basicRes, advancedRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`),
        axios.get(`${API}/stats/advanced`)
      ]);
      setStats(basicRes.data);
      setAdvancedStats(advancedRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const apps = [
    {
      id: 'appraisal',
      title: 'Tasación de Activos',
      description: 'Tasación de camiones y equipos con comparables del mercado',
      icon: Truck,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      hoverColor: 'hover:border-blue-400',
      path: '/apps/appraisal',
      count: stats?.appraisals || 0
    },
    {
      id: 'bank',
      title: 'Análisis Bancario',
      description: 'Análisis de estados bancarios para detectar ingresos reales',
      icon: Building2,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
      hoverColor: 'hover:border-emerald-400',
      path: '/apps/bank-analysis',
      count: stats?.bank_analyses || 0
    },
    {
      id: 'credit',
      title: 'Asignar Riesgo',
      description: 'Evaluación de riesgo y asignación automática de Tier',
      icon: Calculator,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
      hoverColor: 'hover:border-purple-400',
      path: '/apps/credit-evaluation',
      count: stats?.credit_evaluations || 0
    },
    {
      id: 'pricing',
      title: 'Precios y Estructura',
      description: 'Estructuración de financiamientos con escenarios',
      icon: DollarSign,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100 dark:bg-amber-900/30',
      hoverColor: 'hover:border-amber-400',
      path: '/apps/pricing',
      count: stats?.deal_structures || 0
    },
    {
      id: 'summary',
      title: 'Resumen y Decisión',
      description: 'Resumen ejecutivo de crédito y exportación a PDF',
      icon: FileText,
      color: 'text-red-600',
      bgColor: 'bg-red-100 dark:bg-red-900/30',
      hoverColor: 'hover:border-red-400',
      path: '/apps/summary',
      count: stats?.credit_summaries || 0
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  // Format month label for charts
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return monthNames[parseInt(month) - 1] || monthStr;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-slate-900 to-slate-800 p-6 lg:p-8 text-white">
        <div className="relative z-10">
          <h1 className="text-2xl lg:text-3xl font-bold font-['Manrope'] tracking-tight mb-2">
            Herramientas de Análisis de Crédito
          </h1>
          <p className="text-slate-300 mb-4">
            Suite de herramientas independientes para análisis de crédito comercial
          </p>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => navigate('/clients')}
              variant="secondary"
              className="font-semibold"
              data-testid="manage-clients-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              Gestionar Clientes ({stats?.clients || 0})
            </Button>
            <Button 
              onClick={() => navigate('/clients/new')}
              variant="outline"
              className="font-semibold bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Nuevo Cliente
            </Button>
          </div>
        </div>
        <div className="absolute right-0 bottom-0 opacity-10">
          <TrendingUp className="w-48 h-48 lg:w-64 lg:h-64" />
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tasaciones</p>
                <p className="text-2xl font-bold">{advancedStats?.completed_appraisals || 0}</p>
                <p className="text-xs text-muted-foreground">
                  +{advancedStats?.pending_appraisals || 0} pendientes
                </p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Valor Prom. Tasado</p>
                <p className="text-2xl font-bold">${(advancedStats?.avg_appraised_value || 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">
                  vs ${(advancedStats?.avg_requested_price || 0).toLocaleString()} solicitado
                </p>
              </div>
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Alertas de Fraude</p>
                <p className="text-2xl font-bold">{advancedStats?.fraud_alerts_total || 0}</p>
                <p className="text-xs text-amber-600">
                  {advancedStats?.fraud_alerts_this_month || 0} este mes
                </p>
              </div>
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Análisis Bancarios</p>
                <p className="text-2xl font-bold">{advancedStats?.total_bank_analyses || 0}</p>
                <p className="text-xs text-muted-foreground">
                  {advancedStats?.analyses_with_issues || 0} con alertas
                </p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Métricas
          </TabsTrigger>
          <TabsTrigger value="tools" className="gap-2">
            <Truck className="w-4 h-4" />
            Herramientas
          </TabsTrigger>
          <TabsTrigger value="recent" className="gap-2">
            <Clock className="w-4 h-4" />
            Actividad Reciente
          </TabsTrigger>
        </TabsList>

        {/* Metrics Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Appraisals Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Tasaciones por Mes
                </CardTitle>
                <CardDescription>Últimos 6 meses de actividad</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={advancedStats?.monthly_appraisals || []}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis 
                        dataKey="month" 
                        tickFormatter={formatMonth}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value) => [value, 'Tasaciones']}
                        labelFormatter={formatMonth}
                      />
                      <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Asset Type Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="w-5 h-5 text-emerald-600" />
                  Distribución por Tipo de Activo
                </CardTitle>
                <CardDescription>Tipos de vehículos tasados</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={advancedStats?.asset_distribution || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="count"
                        nameKey="type"
                        label={({ type, count }) => `${type}: ${count}`}
                        labelLine={false}
                      >
                        {(advancedStats?.asset_distribution || []).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={ASSET_COLORS[entry.type] || COLORS[index % COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Top Makes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-purple-600" />
                  Top 5 Marcas Más Tasadas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(advancedStats?.top_makes || []).map((make, idx) => {
                    const maxCount = advancedStats?.top_makes?.[0]?.count || 1;
                    const percentage = Math.round((make.count / maxCount) * 100);
                    return (
                      <div key={make.make} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{make.make}</span>
                          <span className="text-muted-foreground">{make.count} tasaciones</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {(!advancedStats?.top_makes || advancedStats.top_makes.length === 0) && (
                    <p className="text-muted-foreground text-center py-8">No hay datos disponibles</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Fraud Alerts Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Resumen de Alertas
                </CardTitle>
                <CardDescription>Estado de validación de documentos</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-center">
                    <p className="text-3xl font-bold text-red-600">{advancedStats?.critical_alerts || 0}</p>
                    <p className="text-sm text-red-700 dark:text-red-400">Alertas Críticas</p>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-center">
                    <p className="text-3xl font-bold text-amber-600">{advancedStats?.warning_alerts || 0}</p>
                    <p className="text-sm text-amber-700 dark:text-amber-400">Advertencias</p>
                  </div>
                </div>
                <div className="mt-4 p-4 rounded-lg border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Análisis con problemas</span>
                    <span className="font-semibold">
                      {advancedStats?.analyses_with_issues || 0} / {advancedStats?.total_bank_analyses || 0}
                    </span>
                  </div>
                  <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-500 rounded-full"
                      style={{ 
                        width: `${advancedStats?.total_bank_analyses > 0 
                          ? (advancedStats.analyses_with_issues / advancedStats.total_bank_analyses * 100) 
                          : 0}%` 
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tools Tab */}
        <TabsContent value="tools">
          <div>
            <h2 className="text-lg font-semibold mb-4 font-['Manrope']">Herramientas Disponibles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {apps.map((app) => (
                <Card 
                  key={app.id}
                  className={cn(
                    "cursor-pointer transition-all duration-200 hover:shadow-lg border-2",
                    app.hoverColor
                  )}
                  onClick={() => navigate(app.path)}
                  data-testid={`app-card-${app.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className={cn('p-3 rounded-lg', app.bgColor)}>
                        <app.icon className={cn('w-6 h-6', app.color)} />
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold">{app.count}</span>
                        <p className="text-xs text-muted-foreground">registros</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardTitle className="text-lg mb-2 font-['Manrope']">{app.title}</CardTitle>
                    <CardDescription className="text-sm">{app.description}</CardDescription>
                    <div className="mt-4 flex items-center text-sm text-primary font-medium">
                      Abrir aplicación
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Recent Activity Tab */}
        <TabsContent value="recent" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Clients */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-['Manrope']">Clientes Recientes</CardTitle>
                  <CardDescription>Últimos clientes agregados</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/clients')}>
                  Ver todos <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {stats?.recent_clients?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_clients.map((client) => (
                      <div 
                        key={client.id}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                        onClick={() => navigate(`/clients/${client.id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{client.full_name}</p>
                            <p className="text-xs text-muted-foreground">{client.email || client.phone || 'Sin contacto'}</p>
                          </div>
                        </div>
                        <Clock className="w-4 h-4 text-muted-foreground" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay clientes recientes</p>
                    <Button variant="link" onClick={() => navigate('/clients/new')}>Agregar cliente</Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Appraisals */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-['Manrope']">Tasaciones Recientes</CardTitle>
                  <CardDescription>Últimas tasaciones realizadas</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/apps/appraisal')}>
                  Ver todas <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {stats?.recent_appraisals?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_appraisals.map((appraisal) => (
                      <div 
                        key={appraisal.id}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                        onClick={() => navigate(`/apps/appraisal/${appraisal.id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <Truck className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{appraisal.asset?.year} {appraisal.asset?.make} {appraisal.asset?.model}</p>
                            <p className="text-xs text-muted-foreground">
                              {appraisal.client_name || 'Sin cliente'} • ${appraisal.appraised_value?.toLocaleString() || 'Pendiente'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay tasaciones recientes</p>
                    <Button variant="link" onClick={() => navigate('/apps/appraisal/new')}>Nueva tasación</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
